/**
 * Event intake form responses, as triaged from the Marketing page.
 *
 * The forms belong to their events (see src/components/EventIntakeFormTab.tsx);
 * these endpoints are the read-and-triage side: tag a response and promote it
 * into the client database, the same way website form leads work.
 */

import { API_BASE } from '../config/apiBase';
import { duplicateClientError } from './clients';
import type { WebsiteFormLeadTag } from './websiteFormLeads';

/** One answer, already resolved from field id to the label people typed under. */
export interface IntakeAnswerCell {
  label: string;
  value: string;
  field_type: string;
}

export interface IntakeResponseRow {
  id: number;
  event_id: number;
  event_name: string;
  event_start_date: string | null;
  form_title: string;
  cells: IntakeAnswerCell[];
  tags: WebsiteFormLeadTag[];
  /** Set once the response has been added to the client database. */
  converted_client_id: number | null;
  converted_company: string | null;
  converted_by_username: string | null;
  converted_at: string | null;
  created_at: string | null;
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw duplicateClientError(res.status, data) ?? new Error((data as { error?: string }).error || fallback);
  }
  return data as T;
}

export async function fetchIntakeFormResponses(userId: number): Promise<IntakeResponseRow[]> {
  const res = await fetch(
    `${API_BASE}/api/marketing/intake-responses?userId=${encodeURIComponent(userId)}`
  );
  const data = await jsonOrThrow<{ responses: IntakeResponseRow[] }>(
    res,
    'Failed to fetch intake form responses'
  );
  return data.responses ?? [];
}

/** Replaces the response's whole tag list. */
export async function updateIntakeResponseTags(
  id: number,
  userId: number,
  tags: WebsiteFormLeadTag[]
): Promise<IntakeResponseRow> {
  const res = await fetch(`${API_BASE}/api/marketing/intake-responses/${id}/tags`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      tags: tags.map((t) => ({ kind: t.kind, label: t.label })),
    }),
  });
  return jsonOrThrow(res, 'Failed to update tags');
}

/** Values shown in the confirmation modal, editable before the lead is created. */
export interface ConvertIntakeResponsePayload {
  user_id: number;
  company: string;
  point_of_contact: string;
  contact_email?: string;
  contact_phone?: string;
  source?: string;
  industry?: string;
  notes?: string;
}

/** Adds the response to the client database as a lead and emails the sales team. */
export async function convertIntakeResponse(
  id: number,
  payload: ConvertIntakeResponsePayload
): Promise<{ response: IntakeResponseRow; client_id: number }> {
  const res = await fetch(`${API_BASE}/api/marketing/intake-responses/${id}/convert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to add lead to the client database');
}
